/**
 * FriendsOfTheYearSection
 *
 * 올해의 친구 섹션
 * - 댓글 교류 Top 10 (자신 제외)
 * - 에디터리얼 디자인 (선 기반, 배경 컨테이너 없음)
 * - MagazineCollectionCard 스타일 호버 효과
 */

import React, { useState, useMemo, useEffect } from 'react';
import { Box, Typography, Dialog, IconButton, Grid, Avatar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import MagazineSectionContainer from '../magazine/MagazineSectionContainer';
import { ConversationView } from '../../components/YearEnd/ConversationView';
import LineGrid from '../magazine/LineGrid';
import { useIsMobile } from '../../hooks/useIsMobile';
import YearEndSectionTitle from './YearEndSectionTitle';
import {
  extractDominantColor,
  getContrastingTextColors,
  createGradientBackground,
} from '../../utils/colorExtraction';
import { getPublicAvatarUrl } from '../../utils/publicAvatarUtils';

// 올해의 친구 영상 URL
const FRIENDS_OF_THE_YEAR_VIDEO = 'https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/yearEnd%2F2025%2Fyear_of_friends.mp4?alt=media';

/**
 * 친구 카드 - 에디터리얼 스타일 호버 효과
 * enrichFriendsWithProfilePictures()에서 설정한 profileImage 사용 (Storage URL)
 */
const FriendCard = ({ friend, rank, size = 'default', isMobile, onClick, t }) => {
  const theme = useTheme();

  const isLarge = size === 'large';
  const username = friend?.username || 'Unknown';
  const friendUserId = friend?.userId || friend?.user_id || friend?.id || '';
  const totalInteractions = friend?.interactionCount || 0;
  const theirReplies = friend?.theirRepliesCount || 0;
  const myReplies = friend?.myRepliesCount || 0;

  // profileImage가 없으면 getPublicAvatarUrl로 fallback
  const profileImageFromData = friend?.profileImage || null;
  const profileImageFromStorage = friendUserId ? getPublicAvatarUrl(friendUserId) : null;
  const profileImage = profileImageFromData || profileImageFromStorage;

  // Avatar 크기
  const avatarSize = isLarge ? (isMobile ? 56 : 72) : (isMobile ? 40 : 48);

  return (
    <Box
      onClick={() => onClick?.(friend)}
      sx={{
        py: isLarge ? (isMobile ? 3 : 4) : (isMobile ? 2 : 3),
        px: isMobile ? 2 : 3,
        cursor: 'pointer',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: theme.palette.primary.main,
          '& *': {
            color: theme.palette.background.paper + ' !important',
          },
        },
      }}
    >
      {/* 우측 상단: 순위 */}
      <Typography
        variant="overline"
        sx={{
          position: 'absolute',
          top: isMobile ? 8 : 12,
          right: isMobile ? 8 : 12,
          color: 'primary.main',
          fontWeight: 900,
          letterSpacing: '0.1em',
        }}
      >
        #{rank}
      </Typography>

      {/* 상단: 아바타 */}
      <Box sx={{ mb: 1 }}>
        <Avatar
          src={profileImage || undefined}
          sx={{
            width: avatarSize,
            height: avatarSize,
            border: '2px solid',
            borderColor: 'divider',
          }}
        >
          {username.charAt(0).toUpperCase()}
        </Avatar>
      </Box>

      {/* 유저네임 */}
      <Typography
        variant={isLarge ? (isMobile ? 'h5' : 'h4') : (isMobile ? 'h6' : 'h6')}
        sx={{
          fontWeight: 700,
          mb: isLarge ? 2 : 1,
          wordBreak: 'break-word',
        }}
      >
        @{username}
      </Typography>

      {/* 교류 통계 */}
      <Box sx={{ mt: 'auto' }}>
        <Typography
          variant={isLarge ? 'h3' : 'h5'}
          sx={{
            fontWeight: 700,
            mb: 0.5,
          }}
        >
          {totalInteractions}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {t('yearEnd.friendsOfTheYear.totalInteractions')} · {t('yearEnd.friendsOfTheYear.received', { count: theirReplies })} · {t('yearEnd.friendsOfTheYear.sent', { count: myReplies })}
        </Typography>
      </Box>
    </Box>
  );
};

export const FriendsOfTheYearSection = ({ friendsOfTheYear, isFriendsLoading, ownerProfile }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile('xl');
  const theme = useTheme();
  const [selectedFriend, setSelectedFriend] = useState(null);

  // 친구별 색상 사전 추출 캐시 (userId -> colorData)
  const [friendColorMap, setFriendColorMap] = useState({});

  // 내 색상 사전 추출 캐시
  const [myColor, setMyColor] = useState({
    bgColor: 'action.selected',
    bgGradient: null,
    textColor: 'inherit',
    isLoading: true,
  });

  // Top 10 친구 (자기 자신은 computeFriendsOfTheYear에서 이미 제외됨)
  // (프로필 이미지는 useYearEndData에서 이미 프리페치됨)
  const filteredFriends = useMemo(() => {
    if (!friendsOfTheYear || !Array.isArray(friendsOfTheYear)) return [];
    // null/undefined 제외하고 Top 10
    return friendsOfTheYear.filter(friend => friend && friend.username).slice(0, 10);
  }, [friendsOfTheYear]);

  // 리포트 주인(owner)의 색상 사전 추출 (ownerProfile이 있으면 바로 추출)
  useEffect(() => {
    const ownerUserId = ownerProfile?.id || '';
    const ownerUsername = ownerProfile?.username || '';

    if (!ownerUserId && !ownerUsername) {
      setMyColor({ bgColor: 'action.selected', bgGradient: null, textColor: 'inherit', isLoading: false });
      return;
    }

    const extractOwnerColor = async () => {
      try {
        // 1순위: Firebase Storage 캐시된 이미지
        if (ownerUserId) {
          const cachedAvatarUrl = getPublicAvatarUrl(ownerUserId, 160);
          if (cachedAvatarUrl) {
            const response = await fetch(cachedAvatarUrl, { method: 'HEAD' });
            if (response.ok) {
              const colorData = await extractDominantColor(cachedAvatarUrl);
              const textColors = getContrastingTextColors(colorData.isDark);
              const gradient = createGradientBackground(colorData.color, colorData.isDark);

              const isValidHex = /^#([A-Fa-f0-9]{6})$/.test(colorData.color);
              const isValidGradient = gradient && typeof gradient === 'string' && gradient.includes('linear-gradient');

              setMyColor({
                bgColor: isValidHex ? colorData.color : 'action.selected',
                bgGradient: isValidGradient ? gradient : null,
                textColor: textColors.primary,
                secondaryColor: textColors.secondary,
                isDark: colorData.isDark,
                isLoading: false,
              });
              return;
            }
          }
        }

        // 2순위: username 기반 색상
        if (ownerUsername) {
          const hash = ownerUsername.split('').reduce((acc, char) => {
            return char.charCodeAt(0) + ((acc << 5) - acc);
          }, 0);
          const hue = Math.abs(hash) % 360;
          const saturation = 50 + (Math.abs(hash >> 8) % 15);
          const lightness = 80 + (Math.abs(hash >> 16) % 10);
          const bgColor = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
          const isDark = lightness < 50;
          const textColors = getContrastingTextColors(isDark);

          setMyColor({
            bgColor,
            bgGradient: null,
            textColor: textColors.primary,
            secondaryColor: textColors.secondary,
            isDark,
            isLoading: false,
          });
          return;
        }

        setMyColor({ bgColor: 'action.selected', bgGradient: null, textColor: 'inherit', isLoading: false });
      } catch (error) {
        console.error('Failed to extract owner color:', error);
        setMyColor({ bgColor: 'action.selected', bgGradient: null, textColor: 'inherit', isLoading: false });
      }
    };

    extractOwnerColor();
  }, [ownerProfile?.id, ownerProfile?.username]);

  // 친구 목록이 결정되면 각 친구별 색상 미리 추출 (모달 열기 전에 준비)
  useEffect(() => {
    if (!filteredFriends || filteredFriends.length === 0) return;

    const extractColorsForFriends = async () => {
      const colorMap = {};

      await Promise.all(
        filteredFriends.map(async (friend) => {
          const friendUserId = friend?.userId || friend?.user_id || friend?.id || '';
          const friendUsername = friend?.username || '';

          if (!friendUserId && !friendUsername) return;

          const key = friendUserId || friendUsername;

          // 이미 추출된 색상이 있으면 스킵
          if (friendColorMap[key]) return;

          try {
            // 1순위: Firebase Storage 캐시된 이미지
            if (friendUserId) {
              const cachedAvatarUrl = getPublicAvatarUrl(friendUserId, 160);
              if (cachedAvatarUrl) {
                const response = await fetch(cachedAvatarUrl, { method: 'HEAD' });
                if (response.ok) {
                  const colorData = await extractDominantColor(cachedAvatarUrl);
                  const textColors = getContrastingTextColors(colorData.isDark);
                  const gradient = createGradientBackground(colorData.color, colorData.isDark);

                  const isValidHex = /^#([A-Fa-f0-9]{6})$/.test(colorData.color);
                  const isValidGradient = gradient && typeof gradient === 'string' && gradient.includes('linear-gradient');

                  colorMap[key] = {
                    bgColor: isValidHex ? colorData.color : 'action.hover',
                    bgGradient: isValidGradient ? gradient : null,
                    textColor: textColors.primary,
                    secondaryColor: textColors.secondary,
                    isDark: colorData.isDark,
                    isLoading: false,
                  };
                  return;
                }
              }
            }

            // 2순위: username 기반 색상
            if (friendUsername) {
              const hash = friendUsername.split('').reduce((acc, char) => {
                return char.charCodeAt(0) + ((acc << 5) - acc);
              }, 0);
              const hue = Math.abs(hash) % 360;
              const saturation = 60 + (Math.abs(hash >> 8) % 20);
              const lightness = 75 + (Math.abs(hash >> 16) % 10);
              const bgColor = `hsl(${hue}, ${saturation}%, ${lightness}%)`;
              const isDark = lightness < 50;
              const textColors = getContrastingTextColors(isDark);

              colorMap[key] = {
                bgColor,
                bgGradient: null,
                textColor: textColors.primary,
                secondaryColor: textColors.secondary,
                isDark,
                isLoading: false,
              };
            }
          } catch (error) {
            console.error('Failed to extract color for friend:', friendUsername, error);
          }
        })
      );

      // 새로 추출된 색상이 있으면 state 업데이트
      if (Object.keys(colorMap).length > 0) {
        setFriendColorMap((prev) => ({ ...prev, ...colorMap }));
      }
    };

    extractColorsForFriends();
  }, [filteredFriends, friendColorMap]);

  // 로딩 중
  if (isFriendsLoading) {
    return (
      <MagazineSectionContainer
        title={t('yearEnd.friendsOfTheYear.title')}
        isMobile={isMobile}
      >
        <Box
          sx={{
            py: 4,
            borderTop: '1px solid',
            borderBottom: '1px solid',
            borderColor: 'primary.main',
            textAlign: 'center',
          }}
        >
          <Typography color="text.secondary">
            {t('yearEnd.friendsOfTheYear.loading')}
          </Typography>
        </Box>
      </MagazineSectionContainer>
    );
  }

  // 데이터 없음 (필터링 후 확인)
  if (!filteredFriends || filteredFriends.length === 0) {
    return null;
  }

  // 상위 5명 (자신 제외된 상태)
  const topFriends = filteredFriends.slice(0, 5);
  // 나머지 6-10위
  const restFriends = filteredFriends.slice(5, 10);

  const handleFriendClick = (friend) => {
    setSelectedFriend(friend);
  };

  return (
    <MagazineSectionContainer isMobile={isMobile}>
      <YearEndSectionTitle title={t('yearEnd.friendsOfTheYear.title')} />

      {/* Top 5 - 에디터리얼 레이아웃 */}
      <Box
        sx={{
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'primary.main',
        }}
      >
        <LineGrid gap={0} borderColor="primary.main" gridColumns={2} breakpoint="xl">
          {/* 1위 - 1/2 공간 차지 + 우측 하단 영상 overlay */}
          {topFriends[0] && (
            <Grid size={{ xs: 12, xl: 6 }}>
              <Box
                sx={{
                  position: 'relative',
                  height: '100%',
                  '&:hover .friend-video': {
                    opacity: 0,
                  },
                }}
              >
                <FriendCard
                  friend={topFriends[0]}
                  rank={1}
                  size="large"
                  isMobile={isMobile}
                  onClick={handleFriendClick}
                  t={t}
                />
                {/* 우측 하단 영상 overlay (부모의 50%, 1:1 비율) - 데스크톱만 */}
                {!isMobile && (
                  <Box
                    sx={{
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      width: '50%',
                      aspectRatio: '1/1',
                      overflow: 'hidden',
                    }}
                  >
                    <video
                      className="friend-video"
                      src={FRIENDS_OF_THE_YEAR_VIDEO}
                      autoPlay
                      loop
                      muted
                      playsInline
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'opacity 0.2s ease-in-out',
                        pointerEvents: 'none',
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
          )}

          {/* 2-5위 - 2X2 Grid */}
          {topFriends.length > 1 && (
            <Grid size={{ xs: 12, xl: 6 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  height: '100%',
                }}
              >
                {topFriends.slice(1, 5).map((friend, index) => (
                  <Box
                    key={friend.userId || friend.username}
                    sx={{
                      borderBottom: index < 2 ? '1px solid' : 'none',
                      borderRight: index % 2 === 0 ? '1px solid' : 'none',
                      borderColor: theme.palette.divider,
                    }}
                  >
                    <FriendCard
                      friend={friend}
                      rank={index + 2}
                      size="small"
                      isMobile={isMobile}
                      onClick={handleFriendClick}
                      t={t}
                    />
                  </Box>
                ))}
              </Box>
            </Grid>
          )}
        </LineGrid>
      </Box>

      {/* 6-10위 */}
      {restFriends.length > 0 && (
        <Box
          sx={{
            borderBottom: '1px solid',
            borderColor: 'primary.main',
            mt: -0.125, // 보더 겹침 보정
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
            }}
          >
            {restFriends.map((friend, index) => (
              <Box
                key={friend.userId || friend.username}
                sx={{
                  borderRight: (isMobile ? index % 2 === 0 : index < 4) ? '1px solid' : 'none',
                  borderBottom: isMobile && index < restFriends.length - 2 ? '1px solid' : 'none',
                  borderColor: theme.palette.divider,
                }}
              >
                <FriendCard
                  friend={friend}
                  rank={index + 6}
                  size="small"
                  isMobile={isMobile}
                  onClick={handleFriendClick}
                  t={t}
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* 대화 모달 */}
      <Dialog
        open={!!selectedFriend}
        onClose={() => setSelectedFriend(null)}
        fullScreen={isMobile}
        maxWidth="sm"
        fullWidth
      >
        {selectedFriend && (
          <Box sx={{ position: 'relative' }}>
            <IconButton
              onClick={() => setSelectedFriend(null)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
              }}
            >
              <CloseIcon />
            </IconButton>
            <ConversationView
              friend={selectedFriend}
              preloadedFriendColor={friendColorMap[selectedFriend?.userId || selectedFriend?.user_id || selectedFriend?.id || selectedFriend?.username]}
              preloadedOwnerColor={myColor}
              ownerInfo={ownerProfile}
            />
          </Box>
        )}
      </Dialog>
    </MagazineSectionContainer>
  );
};

export default FriendsOfTheYearSection;
